import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import { useLabels } from 'wsm-common-data';
import { useDispatch } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { setOffSiteData } from '../actions/offSiteData';
import VehicleCard from './VehicleCard';
import VehicleCardSkeleton from './VehicleCardSkeleton';
import ErrorMessage from './ErrorMessage';

const VehicleCards = ({
	enableCompare,
	enableMediaCarousel,
	inventoryObj,
	isGridLayout,
	isLoading,
	isMyCarsSitePropEnabled,
	isRequesting,
	isTabbedLayout,
	isTransition
}) => {
	const labels = useLabels();
	const dispatch = useDispatch();

	const [offSiteDataState, setOffSiteDataState] = useState({
		offSiteBannerPosition: -1,
		offSiteItemCount: -1
	});
	const [cards, setCards] = useState([]);
	const canUseIntersectionObserver =
		typeof IntersectionObserver !== 'undefined';
	const [cardsViewed, setCardsViewed] = useState([]);
	const cardsViewedRef = useRef(cardsViewed);
	const { offSiteData } = window.DDC.InvData.getFacets() || undefined;

	const dealershipName =
		(isBrowser &&
			window.DDC &&
			window.DDC.dataLayer &&
			window.DDC.dataLayer.dealership &&
			window.DDC.dataLayer.dealership.dealershipName) ||
		'';
	let enableOffSiteToggle = false;
	enableOffSiteToggle = offSiteData && offSiteData.offSiteItemCount !== -1;

	useEffect(() => {
		if (enableOffSiteToggle) {
			setOffSiteDataState({ ...offSiteData });
			dispatch(setOffSiteData(offSiteData));
		}
	}, [offSiteData, dispatch, enableOffSiteToggle]);
	const [showOffSite, setShowOffSite] = useState(
		(sessionStorage.getItem('showOffSite') && true) || false
	);
	let offSiteBannerPosition;
	if (enableOffSiteToggle) {
		// eslint-disable-next-line prefer-destructuring
		offSiteBannerPosition =
			offSiteDataState.offSiteBannerPosition === -1
				? 0
				: offSiteDataState.offSiteBannerPosition;
	}

	const observerRef = useRef(null);

	const observerOptions = {
		rootMargin: '100px'
	};

	const observerCallback = useCallback(
		entries => {
			const newCardsViewed = cardsViewedRef.current;
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					// Only dispatch the event if the WIAPI is available.
					if (isBrowser) {
						window.dispatchEvent(
							new CustomEvent('wsInvListingShown', {
								detail: {
									uuid: entry.target.dataset.uuid
								}
							})
						);
					}
					observerRef.current.unobserve(entry.target);
					if (
						!cardsViewedRef.current.includes(
							entry.target.dataset.uuid
						)
					) {
						newCardsViewed.push(entry.target.dataset.uuid);
					}
				}
			});
			setCardsViewed([...newCardsViewed]);
		},
		[cardsViewedRef, observerRef]
	);

	useEffect(() => {
		if (canUseIntersectionObserver && !observerRef.current) {
			observerRef.current = new IntersectionObserver(
				observerCallback,
				observerOptions
			);
			cardsViewedRef.current = cardsViewed;

			return observerRef.current
				? () => observerRef.current.disconnect()
				: () => {};
		}
		return () => {};
	}, [
		cardsViewed,
		observerOptions,
		observerRef,
		observerCallback,
		canUseIntersectionObserver
	]);

	const { incentives, accounts, inventory, pageInfo } = inventoryObj;
	useEffect(() => {
		if (isLoading) {
			setCards(
				inventory.map(_i => (
					<VehicleCardSkeleton
						key={_i.uuid}
						{...{ isGridLayout, isTabbedLayout }}
					/>
				))
			);
		} else {
			const vehicleCardDefaultProps = {
				accounts,
				incentives,
				pageInfo,
				enableCompare,
				enableMediaCarousel,
				isGridLayout,
				isMyCarsSitePropEnabled,
				isTabbedLayout,
				observerRef
			};
			setCards(
				inventory.map((vehicle, vehicleIndex) => {
					const doFullRender =
						canUseIntersectionObserver === false ||
						cardsViewed.includes(vehicle.uuid) ||
						(vehicleIndex === 0 && !isGridLayout) ||
						(vehicleIndex < 3 && isGridLayout);
					const vehicleCardProps = {
						...vehicleCardDefaultProps,
						vehicle,
						doFullRender
					};
					const isFirstOffsiteCard =
						vehicleIndex === offSiteBannerPosition;
					if (
						enableOffSiteToggle &&
						vehicle.offSite &&
						!isFirstOffsiteCard &&
						!showOffSite
					) {
						return null;
					}
					return (
						<VehicleCard
							{...vehicleCardProps}
							key={vehicle.uuid}
							setShowOffSite={
								enableOffSiteToggle &&
								isFirstOffsiteCard &&
								!showOffSite
									? setShowOffSite
									: undefined
							}
						/>
					);
				})
			);
		}
	}, [
		accounts,
		canUseIntersectionObserver,
		cardsViewed,
		enableCompare,
		enableMediaCarousel,
		enableOffSiteToggle,
		incentives,
		inventory,
		isGridLayout,
		isLoading,
		isMyCarsSitePropEnabled,
		isTabbedLayout,
		observerRef,
		offSiteBannerPosition,
		pageInfo,
		showOffSite
	]);

	return (
		<React.Fragment>
			{enableOffSiteToggle &&
				offSiteBannerPosition === 0 &&
				!showOffSite && (
					<ErrorMessage
						errorLabel={labels
							.get('CLEAR_MESSAGE_OFFSITE')
							.replace('{accountName}', dealershipName)}
					/>
				)}
			<ul
				className={setClassNames([
					isGridLayout ? 'vehicle-card-grid' : 'vehicle-card-list',
					'list-unstyled',
					'transition-property-opacity',
					'transition-duration-sm',
					'transition-timing-function-standard',
					isRequesting || isTransition ? 'opacity-25' : null
				])}
			>
				{cards}
			</ul>
		</React.Fragment>
	);
};

VehicleCards.propTypes = {
	enableCompare: PropTypes.bool.isRequired,
	enableMediaCarousel: PropTypes.bool.isRequired,
	isGridLayout: PropTypes.bool.isRequired,
	isLoading: PropTypes.bool.isRequired,
	isMyCarsSitePropEnabled: PropTypes.bool.isRequired,
	isRequesting: PropTypes.bool.isRequired,
	isTabbedLayout: PropTypes.bool.isRequired,
	isTransition: PropTypes.bool.isRequired,
	inventoryObj: PropTypes.shape({
		incentives: PropTypes.shape({}),
		accounts: PropTypes.shape({}),
		pageInfo: PropTypes.shape({}),
		inventory: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired
};

export default VehicleCards;
